import { z } from 'zod';

import { approvalEntityTypeSchema } from './approval-token.js';

/**
 * Wave 22 — Central Approvals Inbox.
 *
 * A single, cross-project queue of every record waiting on a human
 * decision the PM/admin can act on. Wave 19 Alertas is a triage board
 * (what's getting worse by the day); Wave 22 is a pure "your inbox" —
 * things explicitly submitted and awaiting approval.
 *
 * Items we surface (all scoped to project_members, admin sees all):
 *   - purchase_request  status='pending'    → foreman submitted, PM decides
 *   - purchase_order    status='draft'      → PO being prepared, someone must issue it
 *   - change_order      status='submitted'  → owner/PM must approve/reject
 *   - measurement       status='sent'       → waiting on the approval-token round trip
 *
 * Severity ladder is driven purely by age (daysWaiting):
 *   ≥14d → critical, ≥7d → warning, else info.
 *
 * Financial numbers (amountCents) follow the Wave 17 rule: when the
 * caller only holds read-only roles (client/broker), money is redacted.
 * The inbox is scoped to PM/admin though, so in practice redaction is
 * unreachable — we keep the field so the web can stay uniform with the
 * other aggregate surfaces.
 *
 * We reuse `approvalEntityTypeSchema` from the approval-token module —
 * the four entity types are the same across both concepts.
 */

export const approvalSeveritySchema = z.enum(['critical', 'warning', 'info']);
export type ApprovalSeverity = z.infer<typeof approvalSeveritySchema>;

export const approvalInboxItemSchema = z.object({
  /** Stable id: `${entityType}:${entityId}` for React keys. */
  id: z.string(),
  entityType: approvalEntityTypeSchema,
  entityId: z.string(),
  projectId: z.string(),
  projectName: z.string(),
  /** Short record code (e.g. PR-007, PO-003, CO-001, or period string for medições). */
  code: z.string(),
  /** Record title or human label — what the PM sees to decide. */
  title: z.string(),
  /** ISO timestamp when the record entered this waiting state. */
  submittedAt: z.string(),
  /** Display name of the person who submitted (requester/foreman). */
  requesterName: z.string().nullable(),
  /** Amount at stake — PR estimate, PO total, CO delta, medição total. Nullable when redacted or unknown. */
  amountCents: z.number().int().nullable(),
  severity: approvalSeveritySchema,
  /** Whole-day count since `submittedAt`. Never negative. */
  daysWaiting: z.number().int().nonnegative(),
  /** Deep link target: web router handles it (e.g. `/projects/:id/compras`). */
  href: z.string(),
});
export type ApprovalInboxItem = z.infer<typeof approvalInboxItemSchema>;

export const approvalsInboxCountsSchema = z.object({
  total: z.number().int().nonnegative(),
  critical: z.number().int().nonnegative(),
  warning: z.number().int().nonnegative(),
  info: z.number().int().nonnegative(),
  byType: z.object({
    purchase_request: z.number().int().nonnegative(),
    purchase_order: z.number().int().nonnegative(),
    change_order: z.number().int().nonnegative(),
    measurement: z.number().int().nonnegative(),
  }),
});
export type ApprovalsInboxCounts = z.infer<typeof approvalsInboxCountsSchema>;

export const approvalsInboxProjectCountSchema = z.object({
  projectId: z.string(),
  projectName: z.string(),
  count: z.number().int().nonnegative(),
});
export type ApprovalsInboxProjectCount = z.infer<
  typeof approvalsInboxProjectCountSchema
>;

export const approvalsInboxSchema = z.object({
  counts: approvalsInboxCountsSchema,
  /** Per-project tally, ordered by count desc. Empty when the inbox is empty. */
  byProject: z.array(approvalsInboxProjectCountSchema),
  items: z.array(approvalInboxItemSchema),
  /** True when money columns in the response have been zeroed-out (read-only caller). */
  financialsRedacted: z.boolean(),
  generatedAt: z.string(),
});
export type ApprovalsInbox = z.infer<typeof approvalsInboxSchema>;

/**
 * C13 (#124) — Batch approve / reject from the inbox.
 *
 * `proposalIds` carries the inbox item id encoded as
 * `${entityType}:${entityId}` — same shape as `ApprovalInboxItem.id`.
 * The server walks the list and dispatches each entry to the existing
 * per-entity approve/reject helper. We deliberately keep the wire name
 * `proposalIds` (PT-BR voice across the inbox treats every waiting item
 * as a "proposta") so the frontend can pass `items.map(i => i.id)` raw.
 *
 * Reason is optional because PR/CO reject paths accept a free-form
 * note; PO and measurement approval don't carry one. The server passes
 * `reason` only to the helpers that consume it.
 */
export const approvalsBatchRequestSchema = z.object({
  proposalIds: z
    .array(z.string().min(3).max(200))
    .min(1)
    .max(50),
  action: z.enum(['approve', 'reject']),
  reason: z.string().trim().max(500).optional(),
});
export type ApprovalsBatchRequest = z.infer<
  typeof approvalsBatchRequestSchema
>;

export const approvalsBatchItemResultSchema = z.object({
  id: z.string(),
  ok: z.boolean(),
  /** Stable error code if `ok=false` ('not_found' | 'forbidden' | 'conflict' | 'invalid_id' | 'failed'). */
  error: z.string().optional(),
  /** Human-readable message echoed from the underlying service when available. */
  message: z.string().optional(),
});
export type ApprovalsBatchItemResult = z.infer<
  typeof approvalsBatchItemResultSchema
>;

export const approvalsBatchResponseSchema = z.object({
  results: z.array(approvalsBatchItemResultSchema),
  succeeded: z.number().int().nonnegative(),
  failed: z.number().int().nonnegative(),
});
export type ApprovalsBatchResponse = z.infer<
  typeof approvalsBatchResponseSchema
>;
